import type {RGBA} from '@Utils/color.util';
import type {GradientFillObject} from '@PosterWhiteboard/classes/fill.types';
import type {SerializedShadowOptions} from '@postermywall/fabricjs-2';
import type {TextHorizontalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import type {LIST_STYLE, LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';

export interface TextRunOutlineObject {
  isEnabled: boolean;
  color: RGBA;
  width: number;
}

/**
 * The semantic styles a run may override on top of the item's whole-box `TextStyles`. `null`
 * means "inherit from the item". These are PMW semantics, never raw fabric properties — faux
 * bold and outline both compile down to fabric `stroke`/`strokeWidth`, so storing raw fabric
 * values would make them irrecoverable; the compiler (`text-style-runs-compiler.ts`) owns the
 * semantic → fabric translation.
 */
export interface TextRunStylesObject {
  fontSize: number | null;
  fontFamily: string | null;
  fontLicense: string | null;
  isBold: boolean | null;
  isItalic: boolean | null;
  underLine: boolean | null;
  lineThrough: boolean | null;
  fillColor: RGBA | null;
  fillGradient: TextRunFillGradientObject | null;
  outline: TextRunOutlineObject | null;
  shadow: SerializedShadowOptions | null;
}

/**
 * A per-run gradient fill, laid over the run's bounding box the way a whole-box fill is laid over the box. `fillColor` and `fillGradient` are
 * mutually exclusive overrides — appliers set one and null the other.
 */
export type TextRunFillGradientObject = GradientFillObject;

/**
 * A contiguous range of styled text. `start`/`end` are absolute fabric grapheme indices
 * (`end` exclusive) — the same units fabric uses for `selectionStart`/`selectionEnd` and its
 * per-character styles map, so no re-splitting ever happens on the webapp side.
 */
export interface TextStyleRunObject {
  start: number;
  end: number;
  styles: TextRunStylesObject;
}

/**
 * One line's list membership. `type` disambiguates the two style enums (their numeric values
 * overlap). No gutter is stored: the marker column is a render-time function of the row's font
 * size (the fork's LIST_GUTTER_EM_FACTOR floor), so bullet rows, numbered rows and separate
 * boxes of the same size all start their text at the same column.
 */
export interface TextLineListObject {
  type: LIST_TYPES.ORDERED | LIST_TYPES.UNORDERED;
  style: LIST_STYLE;
  /**
   * Explicit marker color, kept ONLY for legacy posters (migration 80 preserves the bullet color
   * the old editor stored). `null` — every list the current editor creates — means the marker
   * inherits its line's text color at render time (the fork falls back to the line's first
   * character's fill), so markers follow text color like standard editors.
   */
  fill: RGBA | null;
  /**
   * Explicit marker gutter in text units for a row migrated from a legacy whole-box list (the
   * migration's estimate of the width the old editor's bullets textbox reserved). The renderer
   * reserves exactly this from the wrap width — no floor, no live marker measurement — so a
   * migrated row keeps the wrap it has always had. Its text column still DRAWS where the legacy
   * renderer put it: one gap right of the live-measured widest marker, no floor. So neither the
   * wrap nor the text position depends on the estimate; only the box's outer width does. `null` —
   * every list the current editor creates — means the fork sizes the gutter live (shared floor,
   * widened by long ordinals).
   */
  indent: number | null;
}

/**
 * Per-line style overrides, keyed by real (unwrapped) line index. `null` means "inherit from
 * the item". Rendering support lands with the fork's per-line alignment patch; the model and
 * serialization exist from poster version 68.
 */
export interface TextLineStylesObject {
  textAlign: TextHorizontalAlignType | null;
  list: TextLineListObject | null;
}

/**
 * Effective styles across the current text-editing selection (run overrides resolved against
 * the whole-box base), published to Redux for the editing surfaces. A `null` style value means
 * the selection is mixed for that property. `null` state means no text selection is active.
 */
export interface TextSelectionStylesSummary {
  itemId: string;
  isSelectionCollapsed: boolean;
  fontSize: number | null;
  isBold: boolean | null;
  isItalic: boolean | null;
  underLine: boolean | null;
  lineThrough: boolean | null;
  fontFamily: string | null;
  /**
   * The selection's uniform effective solid fill; null when mixed or when the effective fill is
   * a gradient (then `fillGradient` carries it).
   */
  fillColor: RGBA | null;
  /**
   * The selection's uniform effective gradient fill (a run override, or the whole-box gradient
   * where no run overrides it); null when mixed or when the effective fill is solid.
   */
  fillGradient: TextRunFillGradientObject | null;
  outline: TextRunOutlineObject | null;
  /**
   * The selection's uniform effective shadow (a run override, or the item-level shadow where no
   * run overrides it). Null when mixed — surfaces then fall back to the item's own display.
   */
  shadow: SerializedShadowOptions | null;
  /**
   * The uniform effective alignment of every real line the selection touches (a line override,
   * or the whole-box alignment where none). Null when the selection spans mixed alignments.
   */
  textAlign: TextHorizontalAlignType | null;
  /** The uniform list of the lines the selection touches; null when mixed or not a list. */
  lineList: TextLineListObject | null;
}

export const EMPTY_TEXT_RUN_STYLES: TextRunStylesObject = {
  fontSize: null,
  fontFamily: null,
  fontLicense: null,
  isBold: null,
  isItalic: null,
  underLine: null,
  lineThrough: null,
  fillColor: null,
  fillGradient: null,
  outline: null,
  shadow: null,
};
